import { useEffect, useRef, useState } from 'react';
import { useModal } from '../useModal';
import { pbToast } from '../toast';
import { FormFields, FormFooter, initialValuesFromFields } from './FormFields';
import type { FieldSpec } from './FormFields';
import type { OrgChartDoc, Person, Presence, LabelSide, PersonLabelMode } from '../types';

export function progOptions(doc: OrgChartDoc): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  doc.groups.forEach((gr, gi) => {
    gr.programs.forEach((pr, pi) => {
      out.push({ value: `${gi}:${pi}`, label: `${gr.short} — ${pr.short} (${pr.people.length})` });
    });
  });
  return out;
}

export function progLabel(doc: OrgChartDoc, gi: number, pi: number): string {
  const gr = doc.groups[gi];
  if (!gr) return '';
  const pr = gr.programs[pi];
  return gr.programs.length === 1 ? gr.short : `${gr.short} · ${pr ? pr.short : ''}`;
}

export function buildPersonFields(doc: OrgChartDoc, p: Person, gi: number, pi: number): FieldSpec[] {
  const here = `${gi}:${pi}`;
  return [
    { key: 'assign', label: 'Assign to', type: 'select', value: here, options: progOptions(doc) },
    { key: 'vacant', label: 'Vacant seat (open requisition)', type: 'checkbox', value: !!p.vacant },
    {
      key: 'name', label: 'Name', type: 'text', value: p.name || '', placeholder: 'Jane Doe',
      showIf: { key: 'vacant', eq: 'false' },
    },
    { key: 'role', label: 'Role / title', type: 'text', value: p.role || '', placeholder: 'Business Analyst' },
    {
      key: 'req', label: 'Requisition ID', type: 'text', value: p.req || '', placeholder: 'REQ-204',
      showIf: { key: 'vacant', eq: 'true' },
    },
    {
      key: 'ext', label: 'Extension', type: 'text', value: p.ext || '', half: true,
      showIf: { key: 'vacant', eq: 'false' },
    },
    {
      key: 'presence', label: 'Teams presence', type: 'select', value: p.presence || 'available', half: true,
      showIf: { key: 'vacant', eq: 'false' },
      options: [
        { value: 'available', label: 'Available' },
        { value: 'busy', label: 'Busy' },
        { value: 'away', label: 'Away' },
        { value: 'offline', label: 'Offline' },
      ],
    },
    { key: 'task', label: 'Task order', type: 'text', value: p.task || '', placeholder: 'T1+T2' },
    { key: 'lead', label: 'Program lead', type: 'checkbox', value: !!p.lead },
    { section: 'Placement & label' },
    {
      key: 'ang', label: 'Orbit angle °', type: 'number', value: p.ang != null ? Math.round(p.ang) : '',
      half: true, hint: 'Blank = auto', min: -360, max: 360,
    },
    {
      key: 'labelRot', label: 'Label rotation °', type: 'number', value: p.labelRot || 0, half: true,
      min: -180, max: 180, step: 5,
    },
    {
      key: 'labelSide', label: 'Label side', type: 'select', value: p.labelSide || 'auto', half: true,
      options: [
        { value: 'auto', label: 'Auto (clear of the orbit)' },
        { value: 'outward', label: 'Outward from the disc' },
        { value: 'inward', label: 'Inward, over the disc' },
        { value: 'left', label: 'Always left' },
        { value: 'right', label: 'Always right' },
      ],
      hint: 'Applies to the side / auto label style.',
    },
    {
      key: 'labelDist', label: 'Label offset px', type: 'number',
      value: p.labelDist != null ? p.labelDist : '', half: true, min: 4, max: 200,
      hint: 'Blank = chart default',
    },
    {
      key: 'labelSizeOn', label: 'Custom label colour & size', type: 'checkbox',
      value: !!(p.labelColor || p.labelSize),
    },
    {
      key: 'labelColor', label: 'Label colour', type: 'color', value: p.labelColor || doc.settings.groupText,
      half: true, showIf: { key: 'labelSizeOn', eq: 'true' },
    },
    {
      key: 'labelSize', label: 'Name size px', type: 'number', value: p.labelSize || doc.settings.nameSize,
      half: true, min: 6, max: 34, step: 0.5, showIf: { key: 'labelSizeOn', eq: 'true' },
    },
    {
      key: 'labelMode', label: 'Label style', type: 'select', value: p.labelMode || 'inherit',
      options: [
        { value: 'inherit', label: 'Chart default' },
        { value: 'auto', label: 'Side (auto left/right)' },
        { value: 'radial', label: 'Radial — along the spoke' },
        { value: 'tangent', label: 'Tangent — along the orbit' },
        { value: 'above', label: 'Above the dot' },
        { value: 'below', label: 'Below the dot' },
        { value: 'hidden', label: 'Hidden' },
      ],
    },
  ];
}

interface PersonFormValues {
  assign: string;
  vacant: boolean;
  name: string;
  role: string;
  req: string;
  ext: string;
  presence: Presence;
  task: string;
  lead: boolean;
  ang: number | null;
  labelRot: number | null;
  labelSide: LabelSide;
  labelDist: number | null;
  labelSizeOn: boolean;
  labelColor: string;
  labelSize: number | null;
  labelMode: PersonLabelMode;
}

export function buildPersonPatch(v: PersonFormValues, base: Partial<Person>): Person {
  const np = { ...base } as Record<string, unknown>;
  (['name', 'ext', 'presence', 'req', 'lead', 'vacant', 'task'] as string[])
    .forEach((k) => { delete np[k]; });
  np.role = v.role.trim();
  if (v.vacant) {
    np.vacant = true;
    np.req = v.req.trim() || 'REQ-NEW';
  } else {
    np.name = v.name.trim();
    np.ext = v.ext.trim() || '0000';
    np.presence = v.presence;
  }
  if (v.lead) np.lead = true;
  if (v.task.trim()) np.task = v.task.trim();
  if (v.ang != null && !Number.isNaN(v.ang)) np.ang = v.ang; else delete np.ang;
  if (v.labelRot) np.labelRot = v.labelRot; else delete np.labelRot;
  if (v.labelSide && v.labelSide !== 'auto') np.labelSide = v.labelSide; else delete np.labelSide;
  if (v.labelDist != null && !Number.isNaN(v.labelDist)) np.labelDist = v.labelDist; else delete np.labelDist;
  if (v.labelSizeOn) {
    np.labelColor = v.labelColor;
    np.labelSize = v.labelSize;
  } else {
    delete np.labelColor;
    delete np.labelSize;
  }
  if (v.labelMode && v.labelMode !== 'inherit') np.labelMode = v.labelMode; else delete np.labelMode;
  return np as unknown as Person;
}

function PersonEditorFields({ fields, valuesRef }: { fields: FieldSpec[]; valuesRef: React.MutableRefObject<PersonFormValues> }) {
  const [values, setValues] = useState(() => ({ ...valuesRef.current }));
  const onChange = (key: string, value: boolean | number | null | string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value } as unknown as PersonFormValues;
      valuesRef.current = next;
      return next;
    });
  };
  return <FormFields fields={fields} values={values as unknown as Record<string, boolean | number | null | string>} onChange={onChange} idPrefix="person-" />;
}

interface PersonEditorProps {
  doc: OrgChartDoc;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  gi: number;
  pi: number;
  ki?: number;
  isNew?: boolean;
  onClose?: () => void;
}

export default function PersonEditor({ doc, dispatch, gi, pi, ki, isNew = false, onClose }: PersonEditorProps) {
  const { openModal, closeModal, confirm } = useModal();
  const idRef = useRef(`org-person-editor-${gi}:${pi}:${isNew ? 'new' : ki}`);

  useEffect(() => {
    const group = doc.groups[gi];
    const program = group && group.programs[pi];
    const person = isNew
      ? ({ presence: 'available' } as Person)
      : (program ? program.people[ki as number] : undefined);
    if (!person) {
      if (onClose) onClose();
      return undefined;
    }

    const fields = buildPersonFields(doc, person, gi, pi);
    const valuesRef = { current: initialValuesFromFields(fields) as unknown as PersonFormValues };
    const id = idRef.current;

    const handleCancel = () => closeModal(id);

    const handleSave = () => {
      const v = valuesRef.current;
      if (!v.role.trim()) { pbToast('A role or title is required.', 'err'); return; }
      if (!v.vacant && !v.name.trim()) { pbToast('A name is required (or mark the seat vacant).', 'err'); return; }

      const [tgi, tpi] = v.assign.split(':').map(Number);
      const patch = buildPersonPatch(v, isNew ? {} : person);

      if (isNew) {
        dispatch({ type: 'ADD_PERSON', gi: tgi, pi: tpi, person: patch });
        pbToast(`Added ${v.vacant ? 'vacant seat' : patch.name} to ${progLabel(doc, tgi, tpi)}`, 'ok');
      } else if (tgi === gi && tpi === pi) {
        dispatch({ type: 'UPDATE_PERSON', gi, pi, ki, patch });
        pbToast('Employee updated', 'ok');
      } else {
        dispatch({
          type: 'UPDATE_PERSON', gi, pi, ki, targetGi: tgi, targetPi: tpi, patch,
        });
        pbToast(`Moved to ${progLabel(doc, tgi, tpi)}`, 'ok');
      }
      closeModal(id);
    };

    const handleDelete = isNew ? undefined : () => {
      const who = person.vacant ? `the vacant ${person.role} seat` : `${person.name} (${person.role})`;
      if (!confirm(`Remove ${who} from ${progLabel(doc, gi, pi)}?`)) return;
      dispatch({ type: 'DELETE_PERSON', gi, pi, ki });
      pbToast(`Removed ${person.vacant ? 'vacant seat' : person.name}`, 'ok');
      closeModal(id);
    };

    openModal({
      id,
      pageScope: 'page-org',
      title: isNew ? 'Add employee' : (person.vacant ? 'Edit vacant seat' : 'Edit employee'),
      render: (
        <>
          <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: 'var(--mute, rgba(14,26,52,.78))' }}>
            {isNew ? 'New seat on the org chart.' : progLabel(doc, gi, pi)}
          </p>
          <PersonEditorFields fields={fields} valuesRef={valuesRef} />
        </>
      ),
      footer: (
        <FormFooter
          saveLabel={isNew ? 'Add employee' : 'Save changes'}
          deleteLabel="Delete employee"
          onCancel={handleCancel}
          onDelete={handleDelete}
          onSave={handleSave}
        />
      ),
      onClose: () => { if (onClose) onClose(); },
    });

    return () => closeModal(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
